// AutoRulesTab — Rule table with versioning, create/edit modal,
// kill switch per pair and global, simulation panel.

import React, { useState } from 'react';
import {
  Zap, ZapOff, Plus, Edit2, Trash2, AlertTriangle, Play, CheckCircle, XCircle
} from 'lucide-react';

const AutoRulesTab = ({ autoRules = [], onUpdateRules }) => {
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [simulating, setSimulating] = useState(false);

  const [form, setForm] = useState({
    pair: 'USD/KES',
    minAmount: '',
    maxAmount: '',
    maxMargin: '',
    tier: '*',
  });

  const handleToggleRule = (ruleId) => {
    const updated = autoRules.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    onUpdateRules?.(updated);
  };

  const handleDeleteRule = (ruleId) => {
    const updated = autoRules.filter(r => r.id !== ruleId);
    onUpdateRules?.(updated);
  };

  const handleSaveRule = () => {
    const newRule = {
      id: editingRule?.id || `AR-${Date.now()}`,
      pair: form.pair,
      minAmount: Number(form.minAmount),
      maxAmount: Number(form.maxAmount),
      maxMargin: Number(form.maxMargin),
      tier: form.tier,
      enabled: true,
      createdBy: 'Grace Mwangi',
      approvedBy: null,
      version: (editingRule?.version || 0) + 1,
      updatedAt: new Date().toISOString(),
    };

    let updated;
    if (editingRule) {
      updated = autoRules.map(r => r.id === editingRule.id ? newRule : r);
    } else {
      updated = [...autoRules, newRule];
    }
    onUpdateRules?.(updated);
    setShowCreate(false);
    setEditingRule(null);
    setForm({ pair: 'USD/KES', minAmount: '', maxAmount: '', maxMargin: '', tier: '*' });
  };

  const startEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      pair: rule.pair,
      minAmount: String(rule.minAmount),
      maxAmount: String(rule.maxAmount),
      maxMargin: String(rule.maxMargin),
      tier: rule.tier,
    });
    setShowCreate(true);
  };

  const handleSimulate = () => {
    setSimulating(true);
    setTimeout(() => setSimulating(false), 3000);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="space-y-6">
      {/* Global Kill Switch */}
      <div className="bg-white border border-zinc-200 rounded-none p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {globalEnabled ? (
            <Zap className="w-5 h-5 text-green-600" />
          ) : (
            <ZapOff className="w-5 h-5 text-red-600" />
          )}
          <div>
            <p className="font-mono text-xs font-medium">Auto-Quoting Engine</p>
            <p className="text-[10px] text-zinc-500">
              {globalEnabled ? 'Active — matching RFQs will be auto-quoted' : 'Disabled — all auto-quoting suspended'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setGlobalEnabled(!globalEnabled)}
          className={`px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-none transition-colors ${
            globalEnabled
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {globalEnabled ? 'Kill Switch' : 'Enable'}
        </button>
      </div>

      {/* Rules Table */}
      <div className="bg-white border border-zinc-200 rounded-none">
        <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Auto-Quote Rules ({autoRules.length})
          </span>
          <button
            onClick={() => { setEditingRule(null); setShowCreate(true); setForm({ pair: 'USD/KES', minAmount: '', maxAmount: '', maxMargin: '', tier: '*' }); }}
            className="flex items-center gap-1 px-3 py-1 bg-zinc-700 text-white text-xs font-mono uppercase rounded-none hover:bg-zinc-800"
          >
            <Plus className="w-3 h-3" />
            Add Rule
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200">
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Min Amount</th>
                <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Max Amount</th>
                <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Max Margin</th>
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Tier</th>
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Version</th>
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Approved</th>
                <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {autoRules.map(rule => (
                <tr key={rule.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${!rule.enabled ? 'opacity-50' : ''}`}>
                  <td className="py-3 px-3 font-mono text-xs font-medium">{rule.pair}</td>
                  <td className="py-3 px-3 text-right font-mono text-xs">{rule.minAmount?.toLocaleString()}</td>
                  <td className="py-3 px-3 text-right font-mono text-xs">{rule.maxAmount?.toLocaleString()}</td>
                  <td className="py-3 px-3 text-right font-mono text-xs">{rule.maxMargin}</td>
                  <td className="py-3 px-3 font-mono text-xs">{rule.tier === '*' ? 'All' : rule.tier}</td>
                  <td className="py-3 px-3">
                    <button
                      onClick={() => handleToggleRule(rule.id)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none ${
                        rule.enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-zinc-100 text-zinc-500 border-zinc-200'
                      }`}
                    >
                      {rule.enabled ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                      {rule.enabled ? 'Active' : 'Off'}
                    </button>
                  </td>
                  <td className="py-3 px-3 font-mono text-[10px] text-zinc-500">v{rule.version}</td>
                  <td className="py-3 px-3 text-xs text-zinc-500">{rule.approvedBy || '—'}</td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(rule)} className="p-1 hover:bg-zinc-100 rounded-none">
                        <Edit2 className="w-3 h-3 text-zinc-500" />
                      </button>
                      <button onClick={() => handleDeleteRule(rule.id)} className="p-1 hover:bg-zinc-100 rounded-none">
                        <Trash2 className="w-3 h-3 text-zinc-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {autoRules.length === 0 && (
            <div className="text-center py-8 text-zinc-400 text-xs">
              <Zap className="w-6 h-6 mx-auto mb-2" />
              No auto-quote rules configured
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreate && (
        <div className="bg-white border border-zinc-200 rounded-none p-6">
          <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mb-4">
            {editingRule ? `Edit Rule ${editingRule.id}` : 'Create New Rule'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Currency Pair</label>
              <select
                value={form.pair}
                onChange={(e) => setForm(f => ({ ...f, pair: e.target.value }))}
                className="w-full border border-zinc-200 rounded-none px-3 py-2 text-xs font-mono focus:outline-none"
              >
                <option value="*">All Pairs</option>
                <option value="USD/KES">USD/KES</option>
                <option value="EUR/KES">EUR/KES</option>
                <option value="GBP/KES">GBP/KES</option>
                <option value="USD/UGX">USD/UGX</option>
                <option value="EUR/UGX">EUR/UGX</option>
                <option value="GBP/UGX">GBP/UGX</option>
                <option value="USD/TZS">USD/TZS</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Tier</label>
              <select
                value={form.tier}
                onChange={(e) => setForm(f => ({ ...f, tier: e.target.value }))}
                className="w-full border border-zinc-200 rounded-none px-3 py-2 text-xs font-mono focus:outline-none"
              >
                <option value="*">All Tiers</option>
                <option value="tier_1">Tier 1</option>
                <option value="tier_2">Tier 2</option>
                <option value="tier_3">Tier 3</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Min Amount</label>
              <input
                type="number"
                value={form.minAmount}
                onChange={(e) => setForm(f => ({ ...f, minAmount: e.target.value }))}
                className="w-full border border-zinc-200 rounded-none px-3 py-2 text-xs font-mono focus:outline-none"
                placeholder="100000"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Max Amount</label>
              <input
                type="number"
                value={form.maxAmount}
                onChange={(e) => setForm(f => ({ ...f, maxAmount: e.target.value }))}
                className="w-full border border-zinc-200 rounded-none px-3 py-2 text-xs font-mono focus:outline-none"
                placeholder="500000"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] text-zinc-500 block mb-1">Max Margin</label>
              <input
                type="number"
                step="0.0001"
                value={form.maxMargin}
                onChange={(e) => setForm(f => ({ ...f, maxMargin: e.target.value }))}
                className="w-full border border-zinc-200 rounded-none px-3 py-2 text-xs font-mono focus:outline-none"
                placeholder="0.0010"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 p-2 bg-amber-50 border border-amber-200 rounded-none">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-[10px] text-amber-700">Auto-quote rules require senior dealer approval before activation.</span>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSaveRule}
              className="px-4 py-2 bg-zinc-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-zinc-800 rounded-none"
            >
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </button>
            <button
              onClick={() => { setShowCreate(false); setEditingRule(null); }}
              className="px-4 py-2 border border-zinc-200 text-xs font-mono uppercase tracking-wider hover:bg-zinc-50 rounded-none"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Simulation Panel */}
      <div className="bg-white border border-zinc-200 rounded-none p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">Simulation</h3>
            <p className="text-[10px] text-zinc-400 mt-1">Replay last 7 days of RFQs against current rules</p>
          </div>
          <button
            onClick={handleSimulate}
            disabled={simulating}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 rounded-none"
          >
            <Play className="w-4 h-4" />
            {simulating ? 'Running...' : 'Run Simulation'}
          </button>
        </div>
        {simulating && (
          <div className="border border-zinc-200 rounded-none p-4 bg-zinc-50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-zinc-600">Replaying RFQs against active rules...</span>
            </div>
            <div className="w-full h-2 bg-zinc-100 rounded-none overflow-hidden">
              <div className="h-2 bg-zinc-600 rounded-none animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoRulesTab;
